"use client"

import { useState, useEffect, useMemo } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import { Button } from "../ui/button"
import { Input } from "../ui/input"
import { Badge } from "../ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table"
import { Search, ArrowUpDown, ArrowUp, ArrowDown, Loader2, Users, ChevronRight, ChevronDown } from "lucide-react"
import { Pagination } from "../common/pagination-dynamic"
import { apiClient } from "../lib/apiClient"

interface TeamSummaryRow {
  userID: number
  displayName: string
  directManagerName: string | null
  lvl: number
  totalHoursLogged: number
  timesheetRowCount: number
  status: number | null
  lastSubmittedOn: string | null
  expectedWeeks: number
  completedWeeks: number
}

interface TeamMemberTimesheet {
  timesheetID: number
  startDate: string
  endDate: string
  status: number
  hoursLogged: number
  modifiedOn: string | null
}

interface TimesheetLineDetail {
  engagementID: number
  engagementTitle: string | null
  taskID: number
  taskName: string | null
  totalHours: number
  role: string | null
  assignmentStartDate: string | null
  assignmentEndDate: string | null
}

type SortField = "displayName" | "directManagerName" | "lvl" | "totalHoursLogged"
type Period = "week" | "month" | "custom"

function getPeriodRange(period: Period): { start: Date; end: Date } {
  const now = new Date()
  if (period === "month") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1)
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
    return { start, end }
  }
  // Week: Monday - Sunday of the current week
  const start = new Date(now)
  const dayOfWeek = start.getDay() // 0 = Sunday
  const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
  start.setDate(start.getDate() + diffToMonday)
  const end = new Date(start)
  end.setDate(start.getDate() + 6)
  return { start, end }
}

// Local date components, not toISOString() — toISOString() converts to UTC, which can
// silently shift the date by a day depending on the browser's timezone offset.
function formatDate(d: Date): string {
  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${year}-${month}-${day}`
}

// Matches dbo.StatusMaster (confirmed live on prod — TimesheetHdr only ever uses 1-4;
// 5-9 exist in StatusMaster but are Leave-workflow statuses, not timesheet ones).
function statusBadge(status: number | null | undefined) {
  switch (status) {
    case 1:
      return <Badge variant="outline" className="text-gray-600">Pending</Badge>
    case 2:
      return <Badge className="bg-orange-100 text-orange-700 border-orange-200">Submitted</Badge>
    case 3:
      return <Badge className="bg-green-100 text-green-700 border-green-200">Approved</Badge>
    case 4:
      return <Badge className="bg-red-100 text-red-700 border-red-200">Rejected</Badge>
    default:
      return <Badge variant="outline">—</Badge>
  }
}

// Quick visual read on how caught-up someone is for the selected period, independent
// of the Timesheet Status column (which only shows a single status when there's
// exactly one timesheet in range). Based on Submitted-or-Approved weeks vs. how many
// weeks the period actually spans.
function getCompletionHint(row: TeamSummaryRow): { color: string; label: string } {
  if (row.expectedWeeks <= 0) {
    return { color: "bg-gray-300", label: "No period selected" }
  }
  const ratio = row.completedWeeks / row.expectedWeeks
  if (ratio >= 1) {
    return { color: "bg-green-500", label: "All timesheets submitted for this period" }
  }
  if (ratio >= 0.5) {
    return { color: "bg-green-300", label: `${row.completedWeeks} of ${row.expectedWeeks} weeks submitted — a few missing` }
  }
  if (ratio > 0) {
    return { color: "bg-orange-500", label: `${row.completedWeeks} of ${row.expectedWeeks} weeks submitted — several missing` }
  }
  return { color: "bg-red-500", label: "No timesheets submitted for this period" }
}

function getStatusBadge(row: TeamSummaryRow) {
  if (row.timesheetRowCount === 0) {
    return <Badge variant="outline" className="text-gray-500">Not Submitted</Badge>
  }
  if (row.timesheetRowCount > 1) {
    return <Badge variant="outline" className="text-gray-600">{row.timesheetRowCount} timesheets — expand for detail</Badge>
  }
  return statusBadge(row.status)
}

export function TeamSummaryPage() {
  const [rows, setRows] = useState<TeamSummaryRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [period, setPeriod] = useState<Period>("week")
  const [searchTerm, setSearchTerm] = useState("")
  const [sortField, setSortField] = useState<SortField>("displayName")
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc")
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  const [expandedUserId, setExpandedUserId] = useState<number | null>(null)
  const [drillDownLoading, setDrillDownLoading] = useState(false)
  const [drillDownError, setDrillDownError] = useState<string | null>(null)
  const [drillDownTimesheets, setDrillDownTimesheets] = useState<TeamMemberTimesheet[]>([])

  const [expandedTimesheetId, setExpandedTimesheetId] = useState<number | null>(null)
  const [lineDetailLoading, setLineDetailLoading] = useState(false)
  const [lineDetailError, setLineDetailError] = useState<string | null>(null)
  const [lineDetail, setLineDetail] = useState<TimesheetLineDetail[]>([])

  const defaultRange = useMemo(() => getPeriodRange("week"), [])
  const [customStart, setCustomStart] = useState(formatDate(defaultRange.start))
  const [customEnd, setCustomEnd] = useState(formatDate(defaultRange.end))

  const { periodStart, periodEnd } = useMemo(() => {
    if (period === "custom") {
      return { periodStart: customStart, periodEnd: customEnd }
    }
    const { start, end } = getPeriodRange(period)
    return { periodStart: formatDate(start), periodEnd: formatDate(end) }
  }, [period, customStart, customEnd])

  const customRangeInvalid = period === "custom" && (!customStart || !customEnd || customStart > customEnd)

  useEffect(() => {
    if (customRangeInvalid) {
      return
    }

    const fetchSummary = async () => {
      setLoading(true)
      setError(null)
      try {
        const response = await apiClient.get("/TeamSummary", {
          params: { periodStart, periodEnd },
        })
        setRows(Array.isArray(response.data) ? response.data : [])
      } catch (err) {
        console.error("Error fetching team summary:", err)
        setError("Failed to load team summary. Please try again.")
        setRows([])
      } finally {
        setLoading(false)
      }
    }

    fetchSummary()
    // Collapse any open drill-down when the period changes — the fetched detail
    // would no longer match the currently selected date range.
    setExpandedUserId(null)
    setExpandedTimesheetId(null)
  }, [periodStart, periodEnd, customRangeInvalid])

  const toggleDrillDown = async (userId: number) => {
    setExpandedTimesheetId(null)

    if (expandedUserId === userId) {
      setExpandedUserId(null)
      return
    }

    setExpandedUserId(userId)
    setDrillDownLoading(true)
    setDrillDownError(null)
    setDrillDownTimesheets([])
    try {
      const response = await apiClient.get(`/TeamSummary/${userId}/timesheets`, {
        params: { periodStart, periodEnd },
      })
      setDrillDownTimesheets(Array.isArray(response.data) ? response.data : [])
    } catch (err) {
      console.error("Error fetching timesheet detail:", err)
      setDrillDownError("Failed to load timesheet detail.")
    } finally {
      setDrillDownLoading(false)
    }
  }

  const toggleLineDetail = async (e: React.MouseEvent, timesheetId: number) => {
    e.stopPropagation() // don't let this bubble up and re-toggle the parent row's drill-down

    if (expandedTimesheetId === timesheetId) {
      setExpandedTimesheetId(null)
      return
    }

    setExpandedTimesheetId(timesheetId)
    setLineDetailLoading(true)
    setLineDetailError(null)
    setLineDetail([])
    try {
      const response = await apiClient.get(`/TeamSummary/timesheet/${timesheetId}/lines`)
      setLineDetail(Array.isArray(response.data) ? response.data : [])
    } catch (err) {
      console.error("Error fetching timesheet line detail:", err)
      setLineDetailError("Failed to load line-item detail.")
    } finally {
      setLineDetailLoading(false)
    }
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="h-4 w-4" />
    return sortDirection === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
  }

  const filteredAndSortedRows = useMemo(() => {
    const filtered = rows.filter((row) => {
      const term = searchTerm.toLowerCase()
      return (
        row.displayName.toLowerCase().includes(term) ||
        (row.directManagerName ?? "").toLowerCase().includes(term)
      )
    })

    filtered.sort((a, b) => {
      let aValue: any
      let bValue: any
      switch (sortField) {
        case "displayName":
          aValue = a.displayName.toLowerCase()
          bValue = b.displayName.toLowerCase()
          break
        case "directManagerName":
          aValue = (a.directManagerName ?? "").toLowerCase()
          bValue = (b.directManagerName ?? "").toLowerCase()
          break
        case "totalHoursLogged":
          aValue = a.totalHoursLogged
          bValue = b.totalHoursLogged
          break
        default:
          aValue = a.lvl
          bValue = b.lvl
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortDirection === "asc" ? aValue - bValue : bValue - aValue
      }
      if (sortDirection === "asc") {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
      }
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
    })

    return filtered
  }, [rows, searchTerm, sortField, sortDirection])

  const totalPages = Math.max(1, Math.ceil(filteredAndSortedRows.length / pageSize))
  const paginatedRows = filteredAndSortedRows.slice((currentPage - 1) * pageSize, currentPage * pageSize)

  const isSelfOnly = rows.length === 1 && rows[0]?.lvl === 0

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Team Summary
          </h2>
          <p className="text-sm text-slate-600">
            {isSelfOnly
              ? "You have no direct or indirect reports, so this shows your own timesheet summary."
              : "Timesheet summary for your team, up to two levels deep. Click a row to see the individual timesheet(s) behind its status."}
          </p>
          {!isSelfOnly && (
            <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
              <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-green-500" /> All caught up</span>
              <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-green-300" /> A few missing</span>
              <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-orange-500" /> Several missing</span>
              <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500" /> None submitted</span>
            </div>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            <Button
              variant={period === "week" ? "default" : "outline"}
              size="sm"
              onClick={() => { setPeriod("week"); setCurrentPage(1) }}
            >
              This Week
            </Button>
            <Button
              variant={period === "month" ? "default" : "outline"}
              size="sm"
              onClick={() => { setPeriod("month"); setCurrentPage(1) }}
            >
              This Month
            </Button>
            <Button
              variant={period === "custom" ? "default" : "outline"}
              size="sm"
              onClick={() => { setPeriod("custom"); setCurrentPage(1) }}
            >
              Custom Range
            </Button>
          </div>
          {period === "custom" && (
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={customStart}
                onChange={(e) => { setCustomStart(e.target.value); setCurrentPage(1) }}
                className="w-auto"
              />
              <span className="text-gray-500 text-sm">to</span>
              <Input
                type="date"
                value={customEnd}
                onChange={(e) => { setCustomEnd(e.target.value); setCurrentPage(1) }}
                className="w-auto"
              />
            </div>
          )}
          {customRangeInvalid && (
            <p className="text-xs text-red-600">Start date must be on or before end date.</p>
          )}
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name or manager..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1) }}
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-600">Loading team summary...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          ) : filteredAndSortedRows.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-sm">No results found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="min-w-[750px]">
                <TableHeader>
                  <TableRow className="bg-gray-50 border-b border-gray-200">
                    <TableHead className="w-8"></TableHead>
                    <TableHead>
                      <Button variant="ghost" onClick={() => handleSort("displayName")} className="flex items-center gap-1 -ml-3">
                        Name {getSortIcon("displayName")}
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button variant="ghost" onClick={() => handleSort("directManagerName")} className="flex items-center gap-1 -ml-3">
                        Direct Manager {getSortIcon("directManagerName")}
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button variant="ghost" onClick={() => handleSort("lvl")} className="flex items-center gap-1 -ml-3">
                        Level {getSortIcon("lvl")}
                      </Button>
                    </TableHead>
                    <TableHead>
                      <Button variant="ghost" onClick={() => handleSort("totalHoursLogged")} className="flex items-center gap-1 -ml-3">
                        Hours Logged {getSortIcon("totalHoursLogged")}
                      </Button>
                    </TableHead>
                    <TableHead>Timesheet Status</TableHead>
                    <TableHead>Last Submitted</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedRows.map((row) => {
                    const isExpanded = expandedUserId === row.userID
                    return (
                      <>
                        <TableRow
                          key={row.userID}
                          className="border-b border-gray-100 cursor-pointer hover:bg-slate-50"
                          onClick={() => toggleDrillDown(row.userID)}
                        >
                          <TableCell className="text-gray-400">
                            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </TableCell>
                          <TableCell className="font-medium text-gray-900">
                            <div className="flex items-center gap-2">
                              <span
                                className={`inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${getCompletionHint(row).color}`}
                                title={getCompletionHint(row).label}
                              />
                              {row.displayName}
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-700">{row.directManagerName ?? "—"}</TableCell>
                          <TableCell className="text-gray-700">{row.lvl === 0 ? "Self" : row.lvl}</TableCell>
                          <TableCell className="text-gray-700">{row.totalHoursLogged.toFixed(1)}h</TableCell>
                          <TableCell>{getStatusBadge(row)}</TableCell>
                          <TableCell className="text-gray-700">
                            {row.lastSubmittedOn ? new Date(row.lastSubmittedOn).toLocaleDateString() : "—"}
                          </TableCell>
                        </TableRow>
                        {isExpanded && (
                          <TableRow key={`${row.userID}-detail`} className="bg-slate-50 border-b border-gray-100">
                            <TableCell colSpan={7} className="py-3">
                              {drillDownLoading ? (
                                <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                                  <Loader2 className="w-4 h-4 animate-spin" /> Loading timesheets...
                                </div>
                              ) : drillDownError ? (
                                <p className="text-sm text-red-600 py-2">{drillDownError}</p>
                              ) : drillDownTimesheets.length === 0 ? (
                                <p className="text-sm text-gray-500 py-2">No timesheets found for this period.</p>
                              ) : (
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead className="w-8"></TableHead>
                                      <TableHead>Week</TableHead>
                                      <TableHead>Status</TableHead>
                                      <TableHead>Hours</TableHead>
                                      <TableHead>Last Modified</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {drillDownTimesheets.map((ts) => {
                                      const lineDetailExpanded = expandedTimesheetId === ts.timesheetID
                                      return (
                                        <>
                                          <TableRow
                                            key={ts.timesheetID}
                                            className="cursor-pointer hover:bg-slate-100"
                                            onClick={(e) => toggleLineDetail(e, ts.timesheetID)}
                                          >
                                            <TableCell className="text-gray-400">
                                              {lineDetailExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                            </TableCell>
                                            <TableCell className="text-gray-700">
                                              {new Date(ts.startDate).toLocaleDateString()} – {new Date(ts.endDate).toLocaleDateString()}
                                            </TableCell>
                                            <TableCell>{statusBadge(ts.status)}</TableCell>
                                            <TableCell className="text-gray-700">{ts.hoursLogged.toFixed(1)}h</TableCell>
                                            <TableCell className="text-gray-700">
                                              {ts.modifiedOn ? new Date(ts.modifiedOn).toLocaleString() : "—"}
                                            </TableCell>
                                          </TableRow>
                                          {lineDetailExpanded && (
                                            <TableRow key={`${ts.timesheetID}-lines`} className="bg-white">
                                              <TableCell colSpan={5} className="py-3">
                                                {lineDetailLoading ? (
                                                  <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                                                    <Loader2 className="w-4 h-4 animate-spin" /> Loading line items...
                                                  </div>
                                                ) : lineDetailError ? (
                                                  <p className="text-sm text-red-600 py-2">{lineDetailError}</p>
                                                ) : lineDetail.length === 0 ? (
                                                  <p className="text-sm text-gray-500 py-2">No line items found for this timesheet.</p>
                                                ) : (
                                                  <div className="space-y-1">
                                                    <Table>
                                                      <TableHeader>
                                                        <TableRow>
                                                          <TableHead>Engagement</TableHead>
                                                          <TableHead>Task</TableHead>
                                                          <TableHead>Total Hours (this week)</TableHead>
                                                          <TableHead>Role</TableHead>
                                                          <TableHead>Assigned</TableHead>
                                                        </TableRow>
                                                      </TableHeader>
                                                      <TableBody>
                                                        {lineDetail.map((line) => (
                                                          <TableRow key={`${line.engagementID}-${line.taskID}`}>
                                                            <TableCell className="text-gray-700">{line.engagementTitle ?? "—"}</TableCell>
                                                            <TableCell className="text-gray-700">{line.taskName ?? "—"}</TableCell>
                                                            <TableCell className="text-gray-700">{line.totalHours.toFixed(2)}h</TableCell>
                                                            <TableCell className="text-gray-700">{line.role ?? "—"}</TableCell>
                                                            <TableCell className="text-gray-700">
                                                              {line.assignmentStartDate ? new Date(line.assignmentStartDate).toLocaleDateString() : "—"}
                                                              {" – "}
                                                              {line.assignmentEndDate ? new Date(line.assignmentEndDate).toLocaleDateString() : "—"}
                                                            </TableCell>
                                                          </TableRow>
                                                        ))}
                                                      </TableBody>
                                                    </Table>
                                                    <p className="text-xs text-gray-400 pt-1">
                                                      Estimated hours and project phase aren't tracked in the system, so they're not shown here.
                                                    </p>
                                                  </div>
                                                )}
                                              </TableCell>
                                            </TableRow>
                                          )}
                                        </>
                                      )
                                    })}
                                  </TableBody>
                                </Table>
                              )}
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    )
                  })}
                </TableBody>
              </Table>
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalItems={filteredAndSortedRows.length}
                onPageChange={setCurrentPage}
                onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1) }}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
